import { i18n } from '../i18n.js';
import { ArrivalCeremony } from './arrival-ceremony.js';
import { InviteQuest } from './invite-quest.js';
import { RewardScene } from './reward-scene.js';
import { SoundEffects } from './sound-effects.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * UI.InviteWelcome — the invitation BRIEF, the second beat of the arrival
 * ceremony.
 *
 * The sequence an invited player now gets is:
 *   1. the starter grant  — 500 Stardust, gold, with its sound
 *   2. THIS screen        — same design, violet, naming who brought them and
 *                           the matches that unlock the invitation reward
 *   3. the payout         — same design, gold again, when it lands
 *
 * Beat 2 exists because the reward is conditional. Reported: "i went to the
 * flow and as feared i didn't receive any stardust", then "i have to be told
 * about it otherwise as a new user i'd tink it's a bug". A condition nobody
 * states is indistinguishable from a broken feature.
 *
 * It does not evaporate on dismissal — it FLIES INTO THE QUEST RAIL, where
 * `UI.InviteQuest` keeps the same numbers visible for as long as they matter.
 * That is the whole reason a player can close it without losing the thread.
 *
 * It is presentation only. Every number comes from the server's referral state;
 * this screen grants nothing and cannot mint anything. It shows once per
 * account per browser and never blocks: any failure leaves the player in the
 * lobby exactly as before.
 *
 * Contract: tests/unit/ui/inviteWelcome.test.js
 */
let InviteWelcome = {};

InviteWelcome = (function () {

  const OVERLAY_ID = 'invite-welcome-overlay';
  const SEEN_KEY = 'moonfall.invite.welcomed.';
  const REMOVE_FALLBACK_MS = 900;
  /* Where the screen files itself. Desktop has the panel; a phone collapses it
     to the rail button, so the FLIP must aim at whichever is really on screen. */
  const MINIMIZE_TARGETS = ['#lobby-quests-list', '#lobby-quests', '#lobby-quests-btn'];

  function uiText(key, fallback, replacements) {
    try {
      const value = i18n?.t?.(key, replacements);
      if (typeof value === 'string' && value && value !== key) return value;
    } catch (_) { /* i18n not ready */ }
    if (!replacements) return fallback;
    /* BOTH brace styles, double first — `{{name}}` is how ui.json writes it and
       substituting the single form first would turn `{{n}}` into `{3}`. */
    return Object.entries(replacements).reduce(
      (text, [token, value]) => String(text)
        .split(`{{${token}}}`).join(String(value))
        .split(`{${token}}`).join(String(value)),
      String(fallback),
    );
  }

  /* Private modes throw on localStorage, or accept a write and keep nothing, so
     the guard falls back to memory for the life of the page. A welcome that
     re-announces itself every session stops being a welcome. */
  const seenThisPage = new Set();

  function alreadyWelcomed(key) {
    if (seenThisPage.has(key)) return true;
    try { return window.localStorage?.getItem(SEEN_KEY + key) === '1'; } catch (_) { return false; }
  }

  function markWelcomed(key) {
    seenThisPage.add(key);
    try { window.localStorage?.setItem(SEEN_KEY + key, '1'); } catch (_) { /* private mode */ }
  }

  function row(parent, className, text) {
    const el = document.createElement('p');
    el.className = className;
    el.textContent = text;
    parent.appendChild(el);
    return el;
  }

  /**
   * Terms normalised once, so every consumer reads the same numbers.
   *
   * The reward is CARD PACKS now, and the pack count is what the promise is
   * denominated in. A payload that carries only the retired Stardust figure
   * yields 0 packs and therefore no promise at all — a stale row must go quiet
   * rather than print an unfilled placeholder at an arriving player.
   */
  function terms(invitedBy) {
    const done = Math.max(0, Number(invitedBy?.completedMatches) || 0);
    const needed = Math.max(0, Number(invitedBy?.qualifyingMatches) || 0);
    const packs = Math.max(0, Number(invitedBy?.rewardPacks) || 0);
    return { done: Math.min(done, needed || done), needed, packs, remaining: Math.max(0, needed - done) };
  }

  /**
   * The progress track, shared with the quest rail so the two never disagree.
   * One pip per required match — a denominator is what turns "not yet" into a
   * countdown instead of a refusal.
   */
  function buildProgress(done, needed) {
    const track = document.createElement('div');
    track.className = 'invite-welcome-progress';
    track.setAttribute('role', 'progressbar');
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', String(needed));
    track.setAttribute('aria-valuenow', String(Math.min(done, needed)));
    for (let i = 0; i < needed; i += 1) {
      const pip = document.createElement('span');
      pip.className = i < done
        ? 'invite-welcome-pip invite-welcome-pip--done'
        : 'invite-welcome-pip';
      track.appendChild(pip);
    }
    return track;
  }

  function build(invitedBy) {
    const { done, needed, packs, remaining } = terms(invitedBy);
    const inviter = String(invitedBy.inviterName || '').trim();

    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    /* The starter-reward classes carry the look the owner approved; the
       `--brief` modifier is the ONLY thing that differs, and it exists so this
       promise reads as cool violet against the warm gold of a payout. */
    overlay.className = 'starter-reward-overlay invite-welcome-overlay invite-welcome-overlay--brief';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'invite-welcome-title');
    if (RewardScene?.createSparks) overlay.appendChild(RewardScene.createSparks());

    const card = document.createElement('section');
    card.className = 'starter-reward-card invite-welcome-card';

    if (RewardScene?.createMark) card.appendChild(RewardScene.createMark());

    const eyebrow = document.createElement('p');
    eyebrow.className = 'starter-reward-eyebrow invite-welcome-eyebrow';
    eyebrow.textContent = uiText('invite.welcomeEyebrow', 'INVITATION ACCEPTED');
    card.appendChild(eyebrow);

    const title = document.createElement('h2');
    title.id = 'invite-welcome-title';
    title.className = 'starter-reward-title invite-welcome-title';
    title.textContent = inviter
      ? uiText('invite.welcomeTitleNamed', '{{name}} brought you here', { name: inviter })
      : uiText('invite.welcomeTitle', 'Welcome to Moonfall');
    card.appendChild(title);

    row(
      card, 'invite-welcome-body',
      uiText('invite.welcomeBody', 'Your seat is claimed. Here is what happens next.'),
    );

    /* THE CONDITION, STATED. This is the line whose absence made a working
       reward look like a bug. */
    if (packs > 0 && needed > 0) {
      const reward = document.createElement('p');
      reward.className = 'invite-welcome-reward';
      const value = document.createElement('span');
      value.className = 'invite-welcome-reward-amount';
      value.textContent = uiText('invite.welcomeRewardAmount', '+{{packs}} packs', { packs });
      const condition = document.createElement('span');
      condition.className = 'invite-welcome-reward-condition';
      condition.textContent = remaining > 0
        ? uiText('invite.welcomeRewardCondition', 'after {{remaining}} more finished matches', { remaining })
        : uiText('invite.welcomeRewardReady', 'on its way to you');
      reward.append(value, condition);
      card.appendChild(reward);

      card.appendChild(buildProgress(done, needed));
      row(
        card, 'invite-welcome-count',
        uiText('invite.welcomeProgress', '{{done}} of {{needed}} matches finished', { done, needed }),
      );
    }

    const cta = document.createElement('button');
    cta.type = 'button';
    cta.id = 'invite-welcome-cta';
    cta.className = 'starter-reward-cta invite-welcome-cta';
    cta.setAttribute('data-i18n', 'invite.welcomeCta');
    /* The CTA names where the screen is ABOUT to go, because it flies into the
       quest rail rather than vanishing — a player who is not told that has to
       rediscover the condition on their own. */
    cta.textContent = uiText('invite.welcomeCta', 'Track it in my quests');
    card.appendChild(cta);

    overlay.appendChild(card);
    return { overlay, card, cta };
  }

  /**
   * Close the brief by filing it into the quest rail.
   *
   * The removal is guaranteed by a timer as well as by the animation, because
   * an app left behind a modal that never fired `finish` is unusable.
   */
  function dismiss(overlay, card) {
    if (!overlay || !overlay.isConnected) return Promise.resolve();
    overlay.classList.add('invite-welcome-overlay--leaving');
    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      overlay.remove();
      /* The quest rail is where the promise lives from here on, so it has to be
         showing the invitation before the screen that explained it is gone. */
      try { InviteQuest?.refresh?.({ flash: true }); } catch (_) { /* rail absent */ }
    };
    setTimeout(remove, REMOVE_FALLBACK_MS);

    const target = RewardScene?.firstVisible?.(MINIMIZE_TARGETS) || null;
    return Promise.resolve(RewardScene?.minimizeInto?.(card || overlay.firstElementChild, target))
      .catch(() => {})
      .then(remove)
      // Last screen of the arrival sequence: the brief has flown into the
      // quest rail, so anything queued behind the ceremony may run now.
      .finally(() => ArrivalCeremony.release());
  }

  /**
   * Show the brief if this player was invited and has not seen it.
   *
   * `invitedBy` is the server's own referral state for THIS account, so the
   * screen cannot claim a referral that does not exist — the same rule the
   * arrival ticket follows.
   */
  function maybeShow(invitedBy, { key = 'me' } = {}) {
    if (!invitedBy || !document.body) return false;
    if (document.getElementById(OVERLAY_ID)) return false;
    if (alreadyWelcomed(key)) return false;

    const { overlay, card, cta } = build(invitedBy);
    document.body.appendChild(overlay);
    ArrivalCeremony.hold();
    markWelcomed(key);

    /* Same cue as the grant it follows, so the two beats read as one ceremony
       rather than a reward and then an unrelated notice. */
    try { SoundEffects?.play?.('card_reveal'); } catch (_) { /* muted */ }

    const close = () => { void dismiss(overlay, card); };
    cta.addEventListener('click', close);
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      /* A modal that lets focus escape behind itself is a trap in the other
         direction — there is exactly one control, so keep focus on it. */
      if (event.key === 'Tab') {
        event.preventDefault();
        cta.focus();
      }
    });
    try { cta.focus({ preventScroll: true }); } catch (_) { cta.focus(); }
    return true;
  }

  /** Ask the server whether this account was invited, then show. Never blocks. */
  async function checkAndShow() {
    try {
      /* `getProgram`, not `getMyReferralProgram` — the latter is the CONVEX
         function name; the client wrapper exposes it under a shorter name
         (`public/scripts/convex-client.js`). Optional chaining on the wrong one
         resolves to undefined and fails silently, which is exactly how this
         screen never appeared in the real flow while every unit test passed. */
      const result = await MoonfallCloud?.referrals?.getProgram?.();
      const invitedBy = result?.invitedBy;
      if (!invitedBy) return false;
      /* Only the beginning of the relationship gets a brief. Once the reward
         has landed there is nothing left to explain. */
      if (invitedBy.status && invitedBy.status !== 'pending') return false;
      return maybeShow(invitedBy, { key: String(invitedBy.expiresAt || 'me') });
    } catch (_) {
      /* A welcome is decoration: a failed lookup leaves the lobby untouched. */
      return false;
    }
  }

  return { maybeShow, checkAndShow, terms, buildProgress, __dismiss: dismiss };
}());

export { InviteWelcome };
